import {inject, Injectable} from '@angular/core';
import {StorageService} from './storage.service';
import {SupabaseAuthService} from './supabase-auth.service';
import {RegionService} from './region.service';
import {
  buildAccountAppUrl,
  prepareAccountAppOAuth,
} from '../features/account/oauth-callback/oauth-callback.constants';

@Injectable({
  providedIn: 'root',
})
export class AccountService {
  private readonly storageService = inject(StorageService);
  private readonly supabaseAuthService = inject(SupabaseAuthService);
  private readonly regionService = inject(RegionService);

  /**
   * Clear this app's session, then land on mp-auth account chooser.
   * Pre-builds the OAuth authorize URL in the URL *hash* (not query) so
   * Cloudflare WAF does not see nested authorize URLs in the request line.
   */
  async logout() {
    let authorizeUrl: string | null = null;
    let regionCode = 'US';
    try {
      await this.supabaseAuthService.signOutCurrentSession();
    } finally {
      await this.storageService.lfRemove('uid');
      await this.storageService.lfRemove('token');
      await this.storageService.lfRemove('userInfo');
      await this.storageService.lfRemove('orgId');
      await this.storageService.lfRemove('orgName');
      await this.storageService.lfRemove('orgRole');
      try {
        regionCode = (await this.regionService.getRegionCode()) || 'US';
        authorizeUrl = await prepareAccountAppOAuth({
          storageService: this.storageService,
          regionService: this.regionService,
          supabaseAuthService: this.supabaseAuthService,
        });
        // prepareAccountAppOAuth may coerce unset → LOCAL on local builds; re-read.
        regionCode = (await this.regionService.getRegionCode()) || regionCode;
      } catch {
        authorizeUrl = null;
      }
      const params = new URLSearchParams({
        return: `${window.location.origin}/account/login`,
      });
      let target = buildAccountAppUrl(`/auth/continue?${params.toString()}`, regionCode);
      if (authorizeUrl) {
        // Fragment is not sent to the edge — keeps nested authorize URLs off WAF.
        target = `${target}#oauth=${encodeURIComponent(authorizeUrl)}`;
      }
      window.location.href = target;
    }
  }
}
